import {
	IChartApi,
	ISeriesApi,
	ISeriesPrimitive,
	ISeriesPrimitiveAxisView,
	SeriesOptionsMap,
	SeriesType,
	Time,
} from 'lightweight-charts';

class AxisView implements ISeriesPrimitiveAxisView {
    _source: AxisLabels
    _options: Omit<AxisLabelOptions, 'priceAxisLabelData' | 'timeAxisLabelData'>
    _data : Omit<PriceAxisLabelData, 'price'> & {
        text: string,
        value: number,
        horizontal: boolean
    }

    constructor(source: AxisLabels, options: Omit<AxisLabelOptions, 'priceAxisLabelData' | 'timeAxisLabelData'>, data: Omit<PriceAxisLabelData, 'price'> & {
        text: string,
        value: number,
        horizontal: boolean
    }){
        this._source = source
        this._options = options
        this._data = data
    }

    coordinate(): number{
        if(this._data.horizontal){
            return this._source._chart.timeScale().timeToCoordinate(this._data.value as Time) ?? NaN
        }
        return this._source._series.priceToCoordinate(this._data.value) ?? NaN
    }

	text(): string{
        return this._data.text.toString()
    }

	textColor(): string{
        return this._data.textColor ?? ''
    }

	backColor(): string{
        return this._data.backColor ?? ''
    }

	visible?(): boolean {
        const coord = this._source._chart.timeScale().timeToCoordinate(this._data.value as Time) 
        if(coord && Number.isFinite(coord) && (coord < 0 || coord >= (this._data.horizontal ? this._source._chart.timeScale().width() : this._source._series.priceScale().width()))) return false
        return this._options.visible !== undefined ? this._options.visible : true
    }
	tickVisible?(): boolean{
        return this._options.tickVisible !== undefined ? this._options.tickVisible : false
    }
}

export interface PriceAxisLabelData {
    price: number, 
    text: string, 
    textColor?: string, 
    backColor?: string,
}

export interface TimeAxisLabelData {
    time: number, 
    text: string, 
    textColor?: string, 
    backColor?: string,
}

export interface AxisLabelOptions {
	visible?: boolean
    tickVisible?: boolean
    priceAxisLabelData: PriceAxisLabelData[]
    timeAxisLabelData: TimeAxisLabelData[]
}

const defaultOptions: AxisLabelOptions = {
	visible: true,
    tickVisible: false,
    priceAxisLabelData: [],
    timeAxisLabelData: []
};

export class AxisLabels implements ISeriesPrimitive<Time> {
	_chart: IChartApi;
	_series: ISeriesApi<keyof SeriesOptionsMap>;
    _options: AxisLabelOptions
	_priceAxisViews: ISeriesPrimitiveAxisView[];
    _timeAxisViews: ISeriesPrimitiveAxisView[];

	constructor(
		chart: IChartApi,
		series: ISeriesApi<SeriesType>,
		options: Partial<AxisLabelOptions>
	) {
		this._chart = chart;
		this._series = series;
        this._options = {
            ...defaultOptions,
            ...options
        }
        this._priceAxisViews = this._options.priceAxisLabelData.map((priceAxisLabelData) => new AxisView(this, {
            visible: this._options.visible,
            tickVisible: this._options.tickVisible
        }, {
            ...priceAxisLabelData,
            horizontal: false,
            value: priceAxisLabelData.price
        }))
        this._timeAxisViews = this._options.timeAxisLabelData.map((timeAxisLabelData) => new AxisView(this, {
            visible: this._options.visible,
            tickVisible: this._options.tickVisible
        }, {
            ...timeAxisLabelData,
            horizontal: true,
            value: timeAxisLabelData.time 
        }))
	}

    applyOptions(options: Partial<AxisLabelOptions>) {
        this._options = {
            ...this._options,
            ...options
        }
        this._priceAxisViews = this._options.priceAxisLabelData.map((priceAxisLabelData) => new AxisView(this, {
            visible: this._options.visible,
            tickVisible: this._options.tickVisible
        }, {
            ...priceAxisLabelData,
            horizontal: false,
            value: priceAxisLabelData.price
        }))
        this._timeAxisViews = this._options.timeAxisLabelData.map((timeAxisLabelData) => new AxisView(this, {
            visible: this._options.visible,
            tickVisible: this._options.tickVisible
        }, {
            ...timeAxisLabelData,
            horizontal: true,
            value: timeAxisLabelData.time 
        }))
    }

    timeAxisViews() {
        return this._timeAxisViews
    }

    priceAxisViews() {
        return this._priceAxisViews
    }
}
